(ns hypnos.consumer.panels.portrait.portrait
  "The portrait portrait step."
  (:require [goog.fs.url :as url]
            [hypnos.config :as config]
            [hypnos.graphql.amplify]
            [hypnos.i18n :refer [tr]]
            [re-frame.core :as rf]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Events

(rf/reg-event-db
 :portrait/set-file
 (fn [db [_ file]]
   (-> db
       (assoc-in [:portrait :file] file)
       (assoc-in [:portrait :upload-status] nil))))

(rf/reg-sub
 :portrait/current-portrait
 (fn [db _]
   (get-in db [:portrait])))

(rf/reg-sub
 :consult/portrait-issues
 (fn [db _]
   (get-in db [:portrait :issues])))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Helpers

(defn- determine-portrait-status
  "Determine the current state of the portrait-taking process."
  [portrait upload-status issues]
  (let [file?      (:file portrait)
        new?       (nil? upload-status)
        uploading? (= :uploading upload-status)
        uploaded?  (= :uploaded upload-status)
        issues?    (seq issues)]
    (cond
      (and (not file?) new? (not issues?)) :new
      (and file? new? (not issues?))       :picture
      uploading?                           :uploading
      (and file? uploaded? (not issues?))  :no-issue
      (and file? uploaded? issues?)        :issue
      (and file? new? issues?)             :retake)))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Fields

(defn- navigation
  [_state status _nav-fn]
  (let [portrait-upload-status (rf/subscribe [:consumer/portrait-upload-status])]
    (case status
      ;; no picture taken
      :new
      [:div.field.is-grouped
       [:div.field.file
        [:label.upload.control
         [:a.button.is-rounded.is-info
          [:span.icon [:i.mdi.mdi-camera]]
          [:span (tr :ui.placeholder/portrait)]]
         [:input.file-input
          {:type      :file
           :capture   :camera
           :accept    "image/*"
           :id        "portait-front"
           :name      "portait-front"
           :on-change (fn [_]
                        (let [el    (.getElementById js/document "portait-front")
                              file  (aget (.-files el) 0)
                              _name (.-name file)]
                          (rf/dispatch [:consult/set-file file])))}]]]]
      ;; picture was taken but not uploaded yet
      :picture
      [:div.field.is-grouped.narrow
       [:p.control
        [:button.button.is-rounded.is-info
         {:disabled (contains? #{:uploading :uploaded} @portrait-upload-status)
          :on-click (fn [_]
                      (rf/dispatch-sync [:consumer/set-portrait-upload-status :uploading])
                      (rf/dispatch [:amplify/upload-file "HypnosAPI" "/p" :consumer/portrait-uploaded]))}
         (tr :ui.label/upload)]]
       [:div.field.file
        [:label.upload.control
         [:a.button.is-rounded.is-light
          [:span.icon [:i.mdi.mdi-camera]]
          [:span (tr :ui.label/re-take)]]
         [:input.file-input
          {:type      :file
           :capture   :camera
           :accept    "image/*"
           :id        "portait-front"
           :name      "portait-front"
           :on-change (fn [_]
                        (let [el    (.getElementById js/document "portait-front")
                              file  (aget (.-files el) 0)
                              _name (.-name file)]
                          (rf/dispatch-sync [:consult/set-file file])))}]]]]
      ;; currently uploading
      :uploading
      [:div]
      ;; portrait was uploaded and no issues
      :no-issue
      nil
      ;; portrait was uploaded and issues found
      :issue
      [:div.field.is-grouped.narrow
       [:p.control
        [:button.button.is-rounded.is-info
         {:disabled true
          :on-click (fn [_]
                      (rf/dispatch-sync [:consumer/set-portrait-upload-status :uploading])
                      (rf/dispatch [:amplify/upload-file "HypnosAPI" "/p" :consumer/portrait-uploaded]))}
         (tr :ui.label/upload)]]
       ;; the file field itself
       [:div.field.file
        [:label.upload.control
         [:a.button.is-rounded.is-info
          [:span.icon [:i.mdi.mdi-camera]]
          [:span (tr :ui.label/re-take)]]
         [:input.file-input
          {:type      :file
           :capture   :camera
           :accept    "image/*"
           :id        "portait-front"
           :name      "portait-front"
           :on-change (fn [_]
                        (let [el    (.getElementById js/document "portait-front")
                              file  (aget (.-files el) 0)
                              _name (.-name file)]
                          (rf/dispatch-sync [:consult/set-file file])))}]]]]
      ;; new image taken with prior issues
      :retake
      [:div.field.is-grouped.narrow
       [:p.control
        [:button.button.is-rounded.is-info
         {:on-click (fn [_]
                      (rf/dispatch-sync [:consumer/set-portrait-upload-status :uploading])
                      (rf/dispatch [:amplify/upload-file "HypnosAPI" "/p" :consumer/portrait-uploaded]))}
         (tr :ui.label/upload)]]
       ;; the file field itself
       [:div.field.file
        [:label.upload.control
         [:a.button.is-rounded.is-light
          [:span.icon [:i.mdi.mdi-camera]]
          [:span (tr :ui.label/re-take)]]
         [:input.file-input
          {:type      :file
           :capture   :camera
           :accept    "image/*"
           :id        "portait-front"
           :name      "portait-front"
           :on-change (fn [_]
                        (let [el    (.getElementById js/document "portait-front")
                              file  (aget (.-files el) 0)
                              _name (.-name file)]
                          (rf/dispatch-sync [:consult/set-file file])))}]]]])))

(defn- statement
  [status consult]
  (let [portrait-issues (rf/subscribe [:consult/portrait-issues])
        service-type    (:consult/service-type consult)]
    [:article.message.is-info
     (cond
       ;; image without issues
       (= :no-issue status)
       [:div.message-body
        [:p (tr :ui.thank-you/portrait)]
        [:p (tr :ui.thank-you/contact service-type)]]
       ;; issues message
       (or (= :issue status)
           (= :retake status))
       [:div.message-body
        [:p (tr :ui.survey.portrait-issue/statement service-type)]
        [:p (tr :ui.survey.portrait-issue.instructions/title)]
        [:div.content
         [:ul {:type 1}
          (doall
           (for [issue @portrait-issues]
             [:li {:key (str "issue-" issue)} (tr (keyword (str "ui.survey.portrait-issue.instructions/" issue)))]))]]]
       ;; no image (yet)
       :else
       [:div.message-body
        [:p (tr :ui.survey.portrait/statement service-type)]
        [:p (tr :ui.survey.portrait.instructions/title)]
        [:div.content
         [:ul {:type 1}
          [:li [:strong (tr :ui.survey.portrait.instructions/frontal)]]
          [:li (tr :ui.survey.portrait.instructions/eyes)]
          [:li (tr :ui.survey.portrait.instructions/mouth)]]]])]))

(defn- image-field
  "The image field."
  []
  (let [subdomain        (rf/subscribe [:consumer/subdomain])
        current-portrait (rf/subscribe [:portrait/current-portrait])]
    [:figure.image
     [:img {:cross-origin (config/hypnos-url subdomain)
            :src          (if (:file @current-portrait)
                            (url/createObjectUrl (:file @current-portrait))
                            "/images/male-profile-front.png")}]]))

(defn- analyze-portrait
  []
  [:article.message.is-info
   [:div.message-header
    [:p (tr :ui.label/upload-portrait)]]
   [:div.message-body
    [:div.content.has-text-grey.has-text-centered
     [:p [:span.icon.is-large [:i.mdi.mdi-sync.mdi-48px.mdi-spin]]]
     [:p.is-size-5-mobile (tr :ui.portrait-upload/portrait)]]]])

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Step

(defn step
  "The portrait step"
  [state nav-fn]
  (let [consult          (rf/subscribe [:consumer/consult])
        current-portrait (rf/subscribe [:consult/current-portrait])
        upload-status    (rf/subscribe [:consumer/portrait-upload-status])
        issues           (rf/subscribe [:consult/portrait-issues])
        status           (determine-portrait-status @current-portrait @upload-status @issues)]
    [:div.card-content
     (cond
       ;; uploading and analyzing the portrait
       (= :uploading status)
       [analyze-portrait]
       ;; uploaded and no issues
       (= :no-issue status)
       [:form
        {:on-submit (fn [e] (.preventDefault e))}
        ;; statement
        [statement status @consult]]
       ;; else
       :else
       [:form
        {:on-submit (fn [e] (.preventDefault e))}
        ;; statement
        [statement status]
        ;; navigation
        [navigation state status nav-fn]
        ;; image field
        [:hr]
        [image-field state]])]))
